export type User = {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  email_verified: boolean;
  mfa_enabled: boolean;
};

export type Conference = {
  id: string;
  organization_id: string;
  name: string;
  short_name: string;
  public_slug: string;
  time_zone: string;
  starts_on: string | null;
  ends_on: string | null;
  event_format: string;
  is_public: boolean;
};

export type Organization = {
  id: string;
  name: string;
  role: "OWNER" | "ADMIN" | "MEMBER";
  conferences: Conference[];
};

export type SignupResult = {
  authenticated: boolean;
  verification_required: boolean;
  demo_account: boolean;
  user: User;
};

export type ConferencePreview = {
  metrics: {
    submissions: number;
    submitted: number;
    reviews_complete: number;
    reviews_total: number;
    decisions: number;
    registrations: number;
    programme_sessions: number;
  };
  submissions: Array<{ reference: string; title: string; track: string; state: string }>;
  reviews: Array<{ reference: string; title: string; reviewer: string; state: string }>;
  decisions: Array<{ reference: string; title: string; outcome: string; state: string }>;
  registrations: Array<{ reference: string; attendee: string; category: string; state: string; payment: string }>;
  programme: Array<{ title: string; day: string; room: string; starts_at: string; slots: number }>;
  emails: Array<{ subject: string; state: string }>;
  exports: Array<{ kind: string; state: string; expires_at: string }>;
};

export type CommunicationWorkspace = {
  summary: { messages: number; queued: number; delivered: number; needs_attention: number };
  deadlines: Array<{
    id: string;
    title: string;
    kind: "AUTHOR" | "REVIEW";
    status: "UPCOMING" | "ACTIVE" | "CLOSED";
    due_at: string;
    audience_kind: "CORRESPONDING_AUTHORS" | "REVIEWERS";
    suggested_subject: string;
  }>;
  messages: Array<{
    id: string;
    subject: string;
    body_text: string;
    state: string;
    audience_kind: string;
    created_at: string;
    counts: { resolved: number; queued: number; accepted: number; delivered: number; failed: number; suppressed: number; reconciliation: number };
    retryable_recipients: Array<{ id: string; email: string }>;
  }>;
};

export type EmailAudiencePreview = {
  count: number;
  excluded: number;
  examples: Array<{ email: string; state: string }>;
};

export type ConferenceDeletionResult = {
  id: string;
  cancellation_ends_at: string;
};

export type AttentionItem = {
  code: string;
  stage: "home" | "collect" | "review" | "decide" | "publish" | "attendees" | "communicate" | "settings";
  severity: "BLOCKED" | "ACTION" | "UPCOMING";
  title: string;
  detail: string;
  action: { label: string; href: string };
};

export type ConferenceAttention = {
  conference_id: string;
  items: AttentionItem[];
};

export type LaunchCheck = {
  key: string;
  label: string;
  group: "REQUIRED" | "OPERATIONS";
  required: boolean;
  status: "READY" | "ACTION" | "BLOCKED" | "OPTIONAL";
  detail: string;
  action: { label: string; href: string };
};

export type LaunchReadiness = {
  conference_id: string;
  summary: {
    launch_ready: boolean;
    required_ready: number;
    required_total: number;
    blocker_count: number;
    attention_count: number;
  };
  checks: LaunchCheck[];
  test_journeys: Array<{
    key: "public_site" | "author" | "reviewer" | "attendee";
    label: string;
    available: boolean;
    path: string | null;
    detail: string;
  }>;
};

export type ReviewerInvitationInput = {
  email: string;
  opens_at: string;
  closes_at: string;
  expected_work: number;
  track_id?: string;
};

export type ReviewerInvitationResult = {
  id: string;
  email: string;
  state: string;
  delivery_status: "QUEUED" | "ACCEPTED" | "RECONCILIATION" | "FAILED" | "SUPPRESSED";
};

export type ReviewerInvitationPublic = {
  conference: { id: string; name: string; public_slug: string };
  destination: string;
  expected_work: number;
  opens_at: string;
  closes_at: string;
  state: string;
  account_exists: boolean;
  confidentiality_summary: string;
};

export type PublicSubmissionEntry = {
  conference: {
    id: string;
    name: string;
    short_name: string;
    public_slug: string;
  };
  phase: {
    name: string;
    instructions: string;
    opens_at: string;
    closes_at: string;
  };
  availability: "SCHEDULED" | "OPEN" | "CLOSED";
};

export type PublicRegistration = {
  conference: {
    id: string;
    organization_id: string;
    name: string;
    short_name: string;
    public_slug: string;
    starts_on: string | null;
    ends_on: string | null;
    venue: string;
  };
  categories: Array<{
    id: string;
    name: string;
    amount_minor: number;
    currency: string;
    capacity: number | null;
    places_remaining: number | null;
    waitlist_enabled: boolean;
  }>;
};

export type RegistrationResult = {
  id: string;
  reference: string;
  registration_state: string;
  payment_state: string;
  attendance_state: string;
  amount_minor: number;
  currency: string;
};

export type RegistrationWorkspace = {
  summary: { total: number; confirmed: number; awaiting_payment: number; checked_in: number };
  categories: Array<{
    id: string; name: string; currency: string; amount_minor: number; capacity: number | null;
    occupied: number; waitlist_enabled: boolean; is_open: boolean;
  }>;
  registrations: Array<{
    id: string; reference: string; attendee: string; email: string; affiliation: string;
    category: string; registration_state: string; payment_state: string;
    attendance_state: string; amount_minor: number; currency: string;
  }>;
};

export type PublicProgramme = {
  conference: string;
  sessions: Array<{
    title: string; kind: string; description?: string; track_label?: string; day: string; date: string; room: string; location: string;
    starts_at: string; ends_at: string; chairs: string[];
    slots: Array<{
      submission_id?: string; reference?: string; title: string; abstract?: string;
      presenters: string[]; starts_at: string; ends_at: string;
    }>;
  }>;
};

export type ProgrammePresentation = {
  submission_id: string; snapshot_id: string; decision_release_id: string;
  reference: string; title: string; abstract: string; presenters: string[];
};

export type ProgrammeSlot = {
  id: string; submission_id: string | null; reference: string; title: string;
  presenters: string[]; starts_at: string; ends_at: string;
  presenter_registration_state?: "REGISTERED" | "REGISTRATION_PENDING" | "NOT_REGISTERED" | "NOT_LINKED" | "NOT_APPLICABLE";
};

export type ProgrammeChange = { kind: "PRESENTATION"; id: string; title: string };

export type ProgrammeWorkspace = {
  issues: Array<{ session_id?: string; slot_id?: string; submission_id?: string; code: string }>;
  changes?: { has_changes: boolean; added: ProgrammeChange[]; changed: ProgrammeChange[]; removed: ProgrammeChange[] };
  days: Array<{ id: string; date: string; label: string; position: number }>;
  rooms: Array<{ id: string; label: string; public_location: string }>;
  sessions: Array<{
    id: string; revision?: number; title: string; kind: string; day_id: string; day: string;
    description?: string; track_label?: string; chairs?: string[];
    room_id: string; room: string; starts_at: string; ends_at: string;
    slot_count: number; slots: ProgrammeSlot[];
  }>;
  unscheduled_submissions: ProgrammePresentation[];
  published: null | { id: string; version: number; published_at: string };
};

export type ExportDataset = {
  key: "SUBMISSIONS" | "REVIEWS" | "DECISIONS" | "REGISTRATIONS" | "FINANCIAL" | "PROGRAMME" | "PROGRAMME_CALENDAR";
  label: string;
  description: string;
  format: "CSV" | "ICS";
  fields: Array<{ key: string; label: string; recommended: boolean }>;
};

export type ExportWorkspace = {
  datasets: ExportDataset[];
  jobs: Array<{
    id: string;
    kind: ExportDataset["key"];
    state: "QUEUED" | "READY" | "CANCELLED" | "EXPIRED" | "FAILED";
    requested_fields: string[];
    manifest: { row_count?: number; sha256?: string; kind?: string };
    created_at: string;
    expires_at: string;
    download_url: string | null;
  }>;
};

export type AbstractBookWorkspace = {
  can_publish: boolean;
  default_title: string;
  jobs: Array<{
    id: string;
    version: number;
    title: string;
    state: "GENERATED" | "PUBLISHED" | "UNPUBLISHED";
    entry_count: number;
    sha256: string;
    generated_at: string;
    published_at: string | null;
    preview_url: string;
    download_url: string;
    public_url: string | null;
  }>;
};

export type ParticipantConferenceSummary = {
  id: string;
  organization_id: string;
  name: string;
  short_name: string;
  public_slug: string;
  starts_on: string | null;
  ends_on: string | null;
  event_format: string;
  roles: Array<"REVIEWER" | "AUTHOR" | "ATTENDEE" | "PRESENTER">;
  tabs: Array<"overview" | "reviews" | "submissions" | "registration" | "programme" | "documents">;
  next_action: { kind: string; label: string; href: string };
};

export type ParticipantConference = {
  conference: Omit<ParticipantConferenceSummary, "roles" | "tabs" | "next_action"> & { venue: string };
  roles: ParticipantConferenceSummary["roles"];
  tabs: ParticipantConferenceSummary["tabs"];
  reviews: null | {
    invitation: null | { id: string; state: string; expected_work: number; closes_at: string };
    counts: { active: number; completed: number };
    assignments: Array<{ id: string; submission_id: string; reference: string; title: string; state: string }>;
  };
  submissions: Array<{ id: string; reference: string; title: string; state: string; conference_id?: string; organization_id?: string }>;
  registration: null | {
    id: string;
    reference: string;
    category: string;
    registration_state: string;
    payment_state: string;
    attendance_state: string;
    amount_minor: number;
    currency: string;
  };
  programme: null | {
    version: number;
    published_at: string;
    conference: string;
    sessions: Array<{
      title: string;
      kind: string;
      day: string;
      date: string;
      room: string;
      location: string;
      starts_at: string;
      ends_at: string;
      chairs: string[];
      slots: Array<{
        submission_id?: string;
        reference?: string;
        title: string;
        abstract?: string;
        presenters: string[];
        starts_at: string;
        ends_at: string;
      }>;
    }>;
  };
  documents: Array<{ id: string; name: string; kind: string; preview_url: string; download_url: string; synthetic?: boolean }>;
};

export type ReviewerOnboardingInput = {
  full_name: string;
  password: string;
  affiliation: string;
  expertise_tags: string[];
  preferred_capacity: number;
};

export type ReviewerPoolRow = {
  id: string;
  email: string;
  name: string;
  track: { id: string; name: string } | null;
  delivery_state: string;
  response_state: string;
  onboarding_state: string;
  expected_work: number;
  preferred_capacity: number;
  active_assignments: number;
  completed_reviews: number;
};

export type ReviewerPoolResult = {
  summary: { invited: number; accepted: number; active_assignments: number; completed_reviews: number };
  rows: ReviewerPoolRow[];
};

export type ReviewAssignmentWorkspace = {
  assignment_mode?: "MANUAL" | "STUDIO";
  summary: { active: number; completed: number };
  submissions: Array<{
    id: string;
    reference: string;
    title: string;
    state: string;
    track_id: string | null;
    versions?: Array<{ version: number; submitted_at: string; preview_url: string; download_url: string }>;
    documents?: Array<{ id: string; name: string; kind: string; preview_url: string; download_url: string; synthetic?: boolean }>;
  }>;
  review_rounds: Array<{ id: string; name: string; status: string }>;
  assignments: Array<{
    id: string;
    submission_id: string;
    submission_reference: string;
    submission_title: string;
    reviewer_id: string;
    reviewer_email: string;
    review_round_id: string;
    review_round_name: string;
    state: string;
  }>;
  reviews?: Array<{
    id: string;
    assignment_id: string;
    submission_id: string;
    submission_reference: string;
    submission_title: string;
    reviewer_email: string;
    version: number;
    submitted_at: string;
    invalidated_at: string | null;
    scores: Array<{ criterion_id: string; label: string; value: number; display: string }>;
    author_feedback: string;
    committee_comments: string;
  }>;
  accepted_reviewers: Array<{
    invitation_id: string;
    reviewer_id: string;
    name: string;
    email: string;
    active_assignments: number;
    completed_reviews: number;
    expected_work: number;
  }>;
  suggestions: Array<{
    invitation_id: string;
    reviewer_id: string;
    name: string;
    email: string;
    active_assignments: number;
    completed_reviews: number;
    expected_work: number;
    reason: string;
  }>;
  synthetic_kit_available: boolean;
};

export type AssignmentStudioCandidate = {
  invitation_id: string;
  reviewer_id: string;
  name: string;
  email: string;
  active_assignments: number;
  completed_reviews: number;
  expected_work: number;
  preferred_capacity: number;
  score: number;
  reasons: string[];
  expertise_tags: string[];
  eligible: boolean;
};

export type AssignmentStudio = {
  review_round: { id: string; name: string } | null;
  submissions: Array<{
    id: string;
    reference: string;
    title: string;
    track_id: string | null;
    assigned_count: number;
    target_count: number;
    candidates: AssignmentStudioCandidate[];
  }>;
};

export type AssignmentStudioSelection = {
  submission_id: string;
  reviewer_id: string;
  invitation_id: string;
  review_round_id: string;
};

export type DecisionWorkspace = {
  can_release: boolean;
  summary: { active: number; completed: number };
  submissions: Array<ReviewAssignmentWorkspace["submissions"][number] & {
    can_record: boolean;
    can_release: boolean;
    active_review_count: number;
    decision: null | { id: string; version: number; state: "DRAFT" | "RECORDED"; outcome: "ACCEPT" | "REJECT" | "REQUEST_REVISION"; rationale: string; author_message: string; editable_fields: string[]; revision_deadline: string | null; released_at: string | null };
    decision_history: Array<{ id: string; version: number; outcome: "ACCEPT" | "REJECT" | "REQUEST_REVISION"; author_message: string; released_at: string }>;
  }>;
  reviews: NonNullable<ReviewAssignmentWorkspace["reviews"]>;
};

export type SetupTemplatePreview = {
  code: "ABSTRACT_REVIEW" | "ABSTRACT_FINAL_PAPER" | "FULL_PAPER_REVIEW" | "POSTERS_PRESENTATIONS";
  name: string;
  digest: string;
  phases: Array<{ stable_key: string; name: string; kind: string; purpose: string; eligibility: string; required_content: string[]; allowed_actions: string[] }>;
  review: { criteria: string[]; default_count: number };
  communications: string[];
};

export type BulkReviewerResult = {
  summary: Record<string, number>;
  results: Array<{ index: number; email: string; status: string; invitation_id?: string; delivery_state?: string; detail?: string }>;
};

export type TestEmailResult = { delivery_status: string };
export type SubmissionPhaseDraft = {
  definition_id: string;
  version_id: string;
  status: "DRAFT" | "PUBLISHED";
  name: string;
  opens_on: string;
  closes_on: string;
  instructions: string;
};
export type CollectionActivation = {
  ready: boolean;
  entry_path: string;
  phase_status: "DRAFT" | "PUBLISHED" | null;
  workflow_status: "DRAFT" | "PUBLISHED" | null;
};
export type WebsiteSection = {
  stable_key: string;
  type: string;
  heading: string;
  body: string;
  cta_label?: string;
  cta_url?: string;
  asset_key?: string;
  items?: string[];
};
export type WebsiteDraft = {
  id: string;
  version: number;
  status: string;
  template_key: string;
  sections: WebsiteSection[];
  responsive: { hero_alignment?: "LEFT" | "CENTER"; density?: "COMFORTABLE" | "COMPACT" };
  published_at?: string | null;
  unpublished_at?: string | null;
};
export type WebsiteVersionSummary = {
  id: string;
  version: number;
  status: string;
  published_at: string | null;
  unpublished_at: string | null;
};
export type WebsiteWorkspace = {
  draft: WebsiteDraft | null;
  published: WebsiteDraft | null;
  readiness: { ready: boolean; blockers: string[]; warnings: string[] };
  public_path: string;
  versions: WebsiteVersionSummary[];
};
export type PublicWebsite = {
  conference: {
    id: string;
    name: string;
    short_name: string;
    public_slug: string;
    starts_on: string | null;
    ends_on: string | null;
    event_format: string;
    venue: string;
  };
  version: number;
  template: string;
  sections: WebsiteSection[];
  responsive: WebsiteDraft["responsive"];
  entry_points?: {
    submissions: { available: boolean; path: string };
    registration: { available: boolean; path: string };
    programme: { available: boolean; path: string };
  };
  archive_snapshot: boolean;
  isolation: { organizer_code: boolean; application_cookies: boolean; tenant_cache_keyed: boolean; origin_policy?: string };
};
export type PasswordResetRequestResult = { detail: string };
export type LoginResult =
  | { authenticated: true; user: User }
  | { authenticated: false; mfa_required: true; code_available: boolean; passkey_available: boolean };

export type SubmissionWorkspace = {
  id: string;
  organization_id: string;
  conference_id: string;
  reference: string;
  title: string;
  state: string;
  abstract: string;
  keywords: string[];
  responses: Record<string, unknown>;
  revision: number;
  track_id: string | null;
  completion: {
    complete: boolean;
    percent: number;
    items: Array<{ key: string; label: string; complete: boolean }>;
  };
  deadline: { open: boolean; opens_at: string; closes_at: string };
  revision_request: null | {
    round: number;
    state: "OPEN" | "SUBMITTED" | "EXPIRED" | "SUPERSEDED";
    editable_fields: string[];
    deadline: string;
    submitted_at: string | null;
  };
  active_phase: null | { name: string; purpose: string; submit_label: string; open: boolean };
  versions: Array<{ version: number; submitted_at: string; preview_url?: string; download_url?: string }>;
  documents: Array<{ id: string; name: string; version: number; preview_url: string; download_url: string }>;
  decision: null | {
    version: number;
    outcome: "ACCEPT" | "REJECT" | "REQUEST_REVISION";
    message: string;
    editable_fields: string[];
    revision_deadline: string | null;
    released_at: string;
    preview_url: string;
    download_url: string;
  };
  decision_history: Array<{ version: number; outcome: "ACCEPT" | "REJECT" | "REQUEST_REVISION"; message: string; revision_deadline: string | null; released_at: string }>;
};
export type SubmissionInspection = {
  submissions: Array<{
    id: string; reference: string; title: string; state: string; updated_at: string;
    completion: SubmissionWorkspace["completion"];
    warnings: string[];
    documents: Array<{ name: string; preview_url: string; download_url: string }>;
    versions: Array<{ version: number; submitted_at: string; preview_url?: string; download_url?: string }>;
  }>;
};

export type ReviewWorkspace = {
  assignment_id: string;
  organization_id: string;
  conference_id: string;
  state: string;
  submission: {
    id: string;
    reference: string;
    title: string;
    abstract: string;
    keywords: string[];
    versions?: Array<{ version: number; submitted_at: string; preview_url: string; download_url: string }>;
    documents?: Array<{
      id: string;
      name: string;
      kind: string;
      preview_url: string;
      download_url: string;
      synthetic?: boolean;
    }>;
  };
  rubric: { id: string; name: string; criteria: Array<{ key?: string; id?: string; label: string; scale?: number | string[]; required?: boolean; instructions?: string }> };
  draft: { revision: number; answers: Record<string, number | string>; author_feedback: string; committee_comments: string } | null;
};

type SessionResponse = { authenticated: boolean; user: User | null };

let organizationPreload: Promise<Organization[]> | null = null;

function loadOrganizations() {
  if (!organizationPreload) {
    organizationPreload = request<Organization[]>("/api/v1/organizations/").catch((error) => {
      organizationPreload = null;
      throw error;
    });
  }
  return organizationPreload;
}

export const api = {
  session: () => request<SessionResponse>("/api/v1/auth/session/"),
  signup: (body: { first_name: string; last_name: string; email: string; password: string }) =>
    request<SignupResult>("/api/v1/auth/signup/", { method: "POST", body: JSON.stringify(body) }),
  login: (body: { email: string; password: string }) =>
    request<LoginResult>("/api/v1/auth/login/", { method: "POST", body: JSON.stringify(body) }),
  verifyMfa: (code: string, trustDevice: boolean) =>
    request<{ authenticated: true; user: User }>("/api/v1/auth/login/mfa/", { method: "POST", body: JSON.stringify({ code, trust_device: trustDevice }) }),
  verifyDemoEmail: () =>
    request<{ user: User }>("/api/v1/auth/demo/verify-email/", { method: "POST", body: "{}" }),
  logout: () => request<void>("/api/v1/auth/logout/", { method: "POST", body: "{}" }),
  preloadOrganizations: () => loadOrganizations(),
  clearOrganizationPreload: () => { organizationPreload = null; },
  organizations: () => loadOrganizations(),
  createOrganization: async (name: string) => {
    const organization = await request<Organization>("/api/v1/organizations/", { method: "POST", body: JSON.stringify({ name }) });
    organizationPreload = null;
    return organization;
  },
  createConference: async (organizationId: string, body: { name: string; short_name: string; public_slug: string; time_zone: string }) => {
    const conference = await request<Conference>(`/api/v1/organizations/${organizationId}/conferences/`, { method: "POST", body: JSON.stringify(body) });
    organizationPreload = null;
    return conference;
  },
  conferencePreview: (conferenceId: string, organizationId: string) =>
    request<ConferencePreview>(`/api/v1/conferences/${conferenceId}/preview/?organization_id=${organizationId}`),
  conferenceAttention: (conferenceId: string, organizationId: string) =>
    request<ConferenceAttention>(`/api/v1/conferences/${conferenceId}/attention/?organization_id=${organizationId}`),
  launchReadiness: (conferenceId: string, organizationId: string) =>
    request<LaunchReadiness>(`/api/v1/conferences/${conferenceId}/launch-readiness/?organization_id=${organizationId}`),
  previewSetupTemplate: (conferenceId: string, organizationId: string, templateCode: SetupTemplatePreview["code"]) =>
    request<SetupTemplatePreview>(`/api/v1/conferences/${conferenceId}/setup-template/preview/`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId, template_code: templateCode }),
    }),
  applySetupTemplate: (conferenceId: string, organizationId: string, body: { template_code: SetupTemplatePreview["code"]; preview_digest: string; phase_dates: Record<string, { opens_at: string; closes_at: string }>; anonymity: string; review_count: number }, idempotencyKey: string) =>
    request<{ status: string; phase_count: number }>(`/api/v1/conferences/${conferenceId}/setup-template/apply/`, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify({ organization_id: organizationId, ...body }),
    }),
  inviteReviewer: (
    conferenceId: string,
    organizationId: string,
    body: ReviewerInvitationInput,
    idempotencyKey: string,
  ) => request<ReviewerInvitationResult>(`/api/v1/conferences/${conferenceId}/reviewer-invitations/`, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({ organization_id: organizationId, ...body }),
  }),
  reviewerInvitation: (token: string) =>
    request<ReviewerInvitationPublic>(`/api/v1/public/reviewer-invitations/${encodeURIComponent(token)}/`),
  respondToReviewerInvitation: (token: string, body: { response: "ACCEPT" | "DECLINE"; accept_confidentiality: boolean }) =>
    request<{ state: string }>(`/api/v1/public/reviewer-invitations/${encodeURIComponent(token)}/respond/`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  onboardReviewer: (token: string, body: ReviewerOnboardingInput) =>
    request<{ state: string; conference_id: string; destination: string }>(`/api/v1/public/reviewer-invitations/${encodeURIComponent(token)}/onboarding/`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  acceptExistingReviewerInvitation: (token: string) => {
    const [organizationId, conferenceId] = token.split(".", 3);
    if (!organizationId || !conferenceId) return Promise.reject(new Error("Invitation is not active"));
    return request<{ state: string; conference_id: string; destination: string }>(`/api/v1/conferences/${conferenceId}/reviewer-invitations/accept/`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId, token, accept_confidentiality: true }),
    });
  },
  publicSubmissionEntry: (organizationId: string, publicSlug: string) =>
    request<PublicSubmissionEntry>(`/api/v1/public/organizations/${organizationId}/conferences/${encodeURIComponent(publicSlug)}/submission-entry/`),
  startPublicSubmission: (organizationId: string, publicSlug: string, idempotencyKey: string) =>
    request<{ submission: { id: string; organization_id: string; conference_id: string; reference: string; title: string; state: string; revision: number; track_id: string | null; next_action: string }; destination: string }>(`/api/v1/public/organizations/${organizationId}/conferences/${encodeURIComponent(publicSlug)}/submission-entry/start/`, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: "{}",
    }),
  publicRegistration: (organizationId: string, publicSlug: string) =>
    request<PublicRegistration>(`/api/v1/public/organizations/${organizationId}/conferences/${encodeURIComponent(publicSlug)}/registration/`, { credentials: "omit" }),
  registerForConference: (
    conferenceId: string,
    organizationId: string,
    body: { category_id: string; standard_answers: { display_name: string; affiliation: string }; restricted_answers: Record<string, string>; declarations: string[] },
    idempotencyKey: string,
  ) => request<RegistrationResult>(`/api/v1/conferences/${conferenceId}/registrations/`, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({ organization_id: organizationId, ...body }),
  }),
  registrationWorkspace: (conferenceId: string, organizationId: string) =>
    request<RegistrationWorkspace>(`/api/v1/conferences/${conferenceId}/registration/workspace/?organization_id=${organizationId}`),
  createRegistrationCategory: (conferenceId: string, organizationId: string, body: { name: string; currency: string; amount_minor: number; capacity: number | null; waitlist_enabled: boolean; unpaid_holds_capacity: boolean }) =>
    request<{ id: string; price_version_id: string }>(`/api/v1/conferences/${conferenceId}/registration/categories/`, { method: "POST", body: JSON.stringify({ organization_id: organizationId, ...body }) }),
  setRegistrationCategoryOpen: (conferenceId: string, organizationId: string, categoryId: string, open: boolean) =>
    request<{ id: string; is_open: boolean }>(`/api/v1/conferences/${conferenceId}/registration/categories/${categoryId}/${open ? "open" : "close"}/`, { method: "POST", body: JSON.stringify({ organization_id: organizationId }) }),
  recordRegistrationFinance: (conferenceId: string, organizationId: string, registrationId: string, body: { kind: string; amount_minor: number; occurred_on: string; method: string; reference: string; note: string }, idempotencyKey: string) =>
    request<{ id: string; kind: string }>(`/api/v1/conferences/${conferenceId}/registrations/${registrationId}/finance/`, { method: "POST", headers: { "Idempotency-Key": idempotencyKey }, body: JSON.stringify({ organization_id: organizationId, ...body }) }),
  reportRegistrationPayment: (conferenceId: string, organizationId: string, registrationId: string, body: { amount_minor: number; occurred_on: string; method: string; reference: string }, idempotencyKey: string) =>
    request<{ id: string; kind: string }>(`/api/v1/conferences/${conferenceId}/registrations/${registrationId}/report-payment/`, { method: "POST", headers: { "Idempotency-Key": idempotencyKey }, body: JSON.stringify({ organization_id: organizationId, ...body }) }),
  markRegistrationAttendance: (conferenceId: string, organizationId: string, registrationId: string, state: "PRESENT" | "LEFT" | "NOT_MARKED") =>
    request<{ attendance_state: string }>(`/api/v1/conferences/${conferenceId}/registrations/${registrationId}/attendance/`, { method: "POST", body: JSON.stringify({ organization_id: organizationId, state }) }),
  programmeWorkspace: (conferenceId: string, organizationId: string) =>
    request<ProgrammeWorkspace>(`/api/v1/conferences/${conferenceId}/programme/publish/?organization_id=${organizationId}`),
  createProgrammeDay: (conferenceId: string, organizationId: string, body: { date: string; label: string; position: number }) =>
    request<{ id: string }>(`/api/v1/conferences/${conferenceId}/programme/days/`, { method: "POST", body: JSON.stringify({ organization_id: organizationId, ...body }) }),
  createProgrammeRoom: (conferenceId: string, organizationId: string, body: { label: string; public_location: string; private_join_url: string }) =>
    request<{ id: string }>(`/api/v1/conferences/${conferenceId}/programme/rooms/`, { method: "POST", body: JSON.stringify({ organization_id: organizationId, ...body }) }),
  createProgrammeSession: (conferenceId: string, organizationId: string, body: { day_id: string; room_id: string; title: string; session_kind: string; description: string; track_label: string; starts_at: string; ends_at: string; chair_display_names: string[]; position: number }) =>
    request<{ id: string }>(`/api/v1/conferences/${conferenceId}/programme/sessions/`, { method: "POST", body: JSON.stringify({ organization_id: organizationId, ...body }) }),
  createProgrammeSlot: (conferenceId: string, organizationId: string, body: { session_id: string; submission_snapshot_id: string; decision_release_id: string; starts_at: string; ends_at: string; position: number }) =>
    request<{ id: string }>(`/api/v1/conferences/${conferenceId}/programme/slots/`, { method: "POST", body: JSON.stringify({ organization_id: organizationId, ...body }) }),
  updateProgrammeSession: (conferenceId: string, organizationId: string, sessionId: string, body: { revision: number; day_id: string; room_id: string; title: string; session_kind: string; description: string; track_label: string; starts_at: string; ends_at: string; chair_display_names: string[]; position: number }) =>
    request<{ id: string; revision: number }>(`/api/v1/conferences/${conferenceId}/programme/sessions/${sessionId}/`, { method: "PATCH", body: JSON.stringify({ organization_id: organizationId, ...body }) }),
  deleteProgrammeSession: (conferenceId: string, organizationId: string, sessionId: string) =>
    request<void>(`/api/v1/conferences/${conferenceId}/programme/sessions/${sessionId}/`, { method: "DELETE", body: JSON.stringify({ organization_id: organizationId }) }),
  updateProgrammeSlot: (conferenceId: string, organizationId: string, slotId: string, body: { session_id: string; starts_at: string; ends_at: string; position: number }) =>
    request<{ id: string }>(`/api/v1/conferences/${conferenceId}/programme/slots/${slotId}/`, { method: "PATCH", body: JSON.stringify({ organization_id: organizationId, ...body }) }),
  deleteProgrammeSlot: (conferenceId: string, organizationId: string, slotId: string) =>
    request<void>(`/api/v1/conferences/${conferenceId}/programme/slots/${slotId}/`, { method: "DELETE", body: JSON.stringify({ organization_id: organizationId }) }),
  publishProgramme: (conferenceId: string, organizationId: string) =>
    request<{ id: string; version: number }>(`/api/v1/conferences/${conferenceId}/programme/publish/`, { method: "POST", body: JSON.stringify({ organization_id: organizationId }) }),
  unpublishProgramme: (conferenceId: string, organizationId: string, versionId: string) =>
    request<{ id: string; unpublished: boolean }>(`/api/v1/conferences/${conferenceId}/programme/publish/`, { method: "DELETE", body: JSON.stringify({ organization_id: organizationId, version_id: versionId }) }),
  publicProgramme: (conferenceId: string, organizationId: string) =>
    request<PublicProgramme>(`/api/v1/public/conferences/${conferenceId}/programme/?organization_id=${encodeURIComponent(organizationId)}`, { credentials: "omit" }),
  exportWorkspace: (conferenceId: string, organizationId: string) =>
    request<ExportWorkspace>(`/api/v1/conferences/${conferenceId}/exports/?organization_id=${encodeURIComponent(organizationId)}`),
  requestExport: (
    conferenceId: string,
    organizationId: string,
    kind: ExportDataset["key"],
    fields: string[],
  ) => request<{ id: string; state: string }>(`/api/v1/conferences/${conferenceId}/exports/`, {
    method: "POST",
    body: JSON.stringify({ organization_id: organizationId, kind, fields, query_scope: {} }),
  }),
  executeExport: (conferenceId: string, organizationId: string, jobId: string) =>
    request<{ id: string; state: string; manifest: Record<string, unknown> }>(`/api/v1/conferences/${conferenceId}/exports/${jobId}/`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId }),
    }),
  abstractBookWorkspace: (conferenceId: string, organizationId: string) =>
    request<AbstractBookWorkspace>(`/api/v1/conferences/${conferenceId}/abstract-books/?organization_id=${encodeURIComponent(organizationId)}`),
  createAbstractBook: (
    conferenceId: string,
    organizationId: string,
    body: {
      title: string;
      include_authors: boolean;
      include_affiliations: boolean;
      order_by: "REFERENCE" | "TITLE";
      rights_confirmed: boolean;
      consent_statement: string;
    },
  ) => request<{ id: string; sha256: string; state: string }>(`/api/v1/conferences/${conferenceId}/abstract-books/`, {
    method: "POST",
    body: JSON.stringify({ organization_id: organizationId, ...body }),
  }),
  setAbstractBookPublication: (
    conferenceId: string,
    organizationId: string,
    jobId: string,
    action: "publish" | "unpublish",
  ) => request<{ id: string; state: string }>(`/api/v1/conferences/${conferenceId}/abstract-books/${jobId}/`, {
    method: "POST",
    body: JSON.stringify({ organization_id: organizationId, action }),
  }),
  participantConferences: () => request<ParticipantConferenceSummary[]>("/api/v1/my/conferences/"),
  participantConference: (conferenceId: string) =>
    request<ParticipantConference>(`/api/v1/my/conferences/${conferenceId}/`),
  reviewerPool: (conferenceId: string, organizationId: string) =>
    request<ReviewerPoolResult>(`/api/v1/conferences/${conferenceId}/reviewer-pool/?organization_id=${organizationId}`),
  reviewAssignmentWorkspace: (conferenceId: string, organizationId: string, submissionId?: string) => {
    const query = new URLSearchParams({ organization_id: organizationId });
    if (submissionId) query.set("submission_id", submissionId);
    return request<ReviewAssignmentWorkspace>(`/api/v1/conferences/${conferenceId}/review-workspace/?${query}`);
  },
  assignmentStudioPreview: (conferenceId: string, organizationId: string) =>
    request<AssignmentStudio>(`/api/v1/conferences/${conferenceId}/event-pro/assignment-studio/`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId, command: "preview" }),
    }),
  assignmentStudioApply: (conferenceId: string, organizationId: string, selections: AssignmentStudioSelection[]) =>
    request<{ created: number; skipped: number }>(`/api/v1/conferences/${conferenceId}/event-pro/assignment-studio/`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId, command: "apply", selections }),
    }),
  decisionWorkspace: (conferenceId: string, organizationId: string) =>
    request<DecisionWorkspace>(`/api/v1/conferences/${conferenceId}/decision-workspace/?organization_id=${organizationId}`),
  saveDecision: (conferenceId: string, organizationId: string, submissionId: string, body: { outcome: string; rationale: string; author_message: string; editable_fields: string[]; revision_deadline: string | null; correction_reason?: string }) =>
    request<{ id: string; version: number; state: "DRAFT" }>(`/api/v1/conferences/${conferenceId}/submissions/${submissionId}/decisions/`, { method: "POST", body: JSON.stringify({ organization_id: organizationId, ...body }) }),
  recordDecision: (conferenceId: string, organizationId: string, versionId: string, incompleteReviewReason: string) =>
    request<{ id: string; state: "RECORDED" }>(`/api/v1/conferences/${conferenceId}/decisions/${versionId}/record/`, { method: "POST", body: JSON.stringify({ organization_id: organizationId, incomplete_review_reason: incompleteReviewReason }) }),
  releaseDecision: (conferenceId: string, organizationId: string, versionId: string, idempotencyKey: string) =>
    request<{ id: string; released_at: string }>(`/api/v1/conferences/${conferenceId}/decisions/${versionId}/release/`, { method: "POST", headers: { "Idempotency-Key": idempotencyKey }, body: JSON.stringify({ organization_id: organizationId }) }),
  createSyntheticReviewKit: (
    conferenceId: string,
    organizationId: string,
    body: { reviewer_id: string; invitation_id: string },
  ) => request<{ submissions_created: number; assignments_created: number }>(`/api/v1/conferences/${conferenceId}/review-workspace/synthetic-kit/`, {
    method: "POST",
    body: JSON.stringify({ organization_id: organizationId, ...body }),
  }),
  assignReviewer: (conferenceId: string, organizationId: string, body: { submission_id: string; reviewer_id: string; invitation_id: string; review_round_id: string }) =>
    request<{ id: string; state: string }>(`/api/v1/conferences/${conferenceId}/submissions/${body.submission_id}/review-assignments/`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId, reviewer_id: body.reviewer_id, invitation_id: body.invitation_id, review_round_id: body.review_round_id }),
    }),
  bulkInviteReviewers: (
    conferenceId: string,
    organizationId: string,
    body: { records: Array<{ email: string; name?: string; track_id?: string }>; opens_at: string; closes_at: string; expected_work: number },
    idempotencyKey: string,
  ) => request<BulkReviewerResult>(`/api/v1/conferences/${conferenceId}/reviewer-invitations/bulk/`, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({ organization_id: organizationId, ...body }),
  }),
  sendTestEmail: (conferenceId: string, organizationId: string, idempotencyKey: string) =>
    request<TestEmailResult>(`/api/v1/conferences/${conferenceId}/emails/test/`, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify({ organization_id: organizationId }),
    }),
  communicationWorkspace: (conferenceId: string, organizationId: string) =>
    request<CommunicationWorkspace>(`/api/v1/conferences/${conferenceId}/emails/?organization_id=${organizationId}`),
  createEmailDraft: (conferenceId: string, organizationId: string, body: { subject: string; body_text: string; audience: { kind: string } }) =>
    request<{ id: string }>(`/api/v1/conferences/${conferenceId}/emails/`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId, ...body }),
    }),
  previewEmailDraft: (conferenceId: string, organizationId: string, draftId: string) =>
    request<EmailAudiencePreview>(`/api/v1/conferences/${conferenceId}/emails/${draftId}/preview/`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId }),
    }),
  queueEmailDraft: (conferenceId: string, organizationId: string, draftId: string, idempotencyKey: string) =>
    request<{ id: string; state: string }>(`/api/v1/conferences/${conferenceId}/emails/${draftId}/queue/`, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify({ organization_id: organizationId }),
    }),
  retryEmailRecipient: (conferenceId: string, organizationId: string, draftId: string, recipientId: string) =>
    request<{ id: string; state: string }>(`/api/v1/conferences/${conferenceId}/emails/${draftId}/recipients/${recipientId}/retry/`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId }),
    }),
  submissionPhase: (conferenceId: string, organizationId: string) =>
    request<{ phase: SubmissionPhaseDraft | null }>(`/api/v1/conferences/${conferenceId}/phases/?organization_id=${organizationId}`)
      .then(({ phase }) => phase),
  collectionStatus: (conferenceId: string, organizationId: string) =>
    request<CollectionActivation>(`/api/v1/conferences/${conferenceId}/collection/activate/?organization_id=${organizationId}`),
  activateCollection: (conferenceId: string, organizationId: string) =>
    request<CollectionActivation>(`/api/v1/conferences/${conferenceId}/collection/activate/`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId }),
    }),
  saveSubmissionPhase: (
    conferenceId: string,
    organizationId: string,
    body: Pick<SubmissionPhaseDraft, "name" | "opens_on" | "closes_on" | "instructions">,
  ) => request<{ phase: SubmissionPhaseDraft }>(`/api/v1/conferences/${conferenceId}/phases/`, {
    method: "POST",
    body: JSON.stringify({
      organization_id: organizationId,
      stable_key: "submissions",
      kind: "AUTHOR",
      sequence: 1,
      purpose: "NEW_SUBMISSION",
      eligibility: "ALL_NEW",
      required_content: ["TITLE", "ABSTRACT", "AUTHORS"],
      allowed_actions: ["CREATE", "EDIT_TEXT", "EDIT_AUTHORS", "UPLOAD"],
      ...body,
    }),
  }).then(({ phase }) => phase),
  websiteWorkspace: (conferenceId: string, organizationId: string) =>
    request<WebsiteWorkspace>(`/api/v1/conferences/${conferenceId}/conference-plus/website/?organization_id=${organizationId}`),
  saveWebsiteDraft: (
    conferenceId: string,
    organizationId: string,
    body: Pick<WebsiteDraft, "template_key" | "sections" | "responsive">,
  ) => request<{ id: string; version: number }>(`/api/v1/conferences/${conferenceId}/conference-plus/website/`, {
    method: "POST",
    body: JSON.stringify({ command: "save", organization_id: organizationId, ...body }),
  }),
  publishWebsite: (conferenceId: string, organizationId: string, websiteId: string) =>
    request<{ id: string; status: string }>(`/api/v1/conferences/${conferenceId}/conference-plus/website/`, {
      method: "POST",
      body: JSON.stringify({ command: "publish", organization_id: organizationId, website_id: websiteId }),
    }),
  unpublishWebsite: (conferenceId: string, organizationId: string, websiteId: string) =>
    request<{ id: string; unpublished: boolean }>(`/api/v1/conferences/${conferenceId}/conference-plus/website/`, {
      method: "POST",
      body: JSON.stringify({ command: "unpublish", organization_id: organizationId, website_id: websiteId }),
    }),
  restoreWebsiteVersion: (conferenceId: string, organizationId: string, websiteId: string) =>
    request<{ id: string; version: number }>(`/api/v1/conferences/${conferenceId}/conference-plus/website/`, {
      method: "POST",
      body: JSON.stringify({ command: "restore", organization_id: organizationId, website_id: websiteId }),
    }),
  publicWebsite: (organizationId: string, publicSlug: string) =>
    request<PublicWebsite>(`/api/v1/public/sites/${encodeURIComponent(publicSlug)}/?organization_id=${encodeURIComponent(organizationId)}`, { credentials: "omit" }),
  requestPasswordReset: (email: string) =>
    request<PasswordResetRequestResult>("/api/v1/auth/password-reset/request/", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  confirmPasswordReset: (uid: string, token: string, password: string) =>
    request<void>("/api/v1/auth/password-reset/confirm/", {
      method: "POST",
      body: JSON.stringify({ uid, token, password }),
    }),
  requestConferenceDeletion: (conferenceId: string, organizationId: string, reason: string) =>
    request<ConferenceDeletionResult>(`/api/v1/conferences/${conferenceId}/billing/`, {
      method: "POST",
      body: JSON.stringify({ command: "request_deletion", organization_id: organizationId, reason }),
    }),
  submissionWorkspace: (conferenceId: string, submissionId: string, organizationId: string) =>
    request<SubmissionWorkspace>(`/api/v1/conferences/${conferenceId}/submissions/${submissionId}/?organization_id=${organizationId}`),
  saveSubmission: (workspace: SubmissionWorkspace, body: { title: string; abstract: string; keywords: string[] }) =>
    request<SubmissionWorkspace>(`/api/v1/conferences/${workspace.conference_id}/submissions/${workspace.id}/save/`, { method: "POST", body: JSON.stringify({ organization_id: workspace.organization_id, expected_revision: workspace.revision, responses: workspace.responses, ...body }) }),
  finalizeSubmission: (workspace: SubmissionWorkspace) =>
    request<{ reference: string; submitted_at: string }>(`/api/v1/conferences/${workspace.conference_id}/submissions/${workspace.id}/finalize/`, { method: "POST", headers: { "Idempotency-Key": `web-finalize-${workspace.id}-${workspace.revision}` }, body: JSON.stringify({ organization_id: workspace.organization_id }) }),
  submitSubmissionRevision: (workspace: SubmissionWorkspace) =>
    request<SubmissionWorkspace>(`/api/v1/conferences/${workspace.conference_id}/submissions/${workspace.id}/revisions/submit/`, { method: "POST", headers: { "Idempotency-Key": `web-revision-${workspace.id}-${workspace.revision}` }, body: JSON.stringify({ organization_id: workspace.organization_id, expected_revision: workspace.revision }) }),
  submitSubmissionPhase: (workspace: SubmissionWorkspace) =>
    request<{ snapshot_id: string; version: number; submitted_at: string }>(`/api/v1/conferences/${workspace.conference_id}/submissions/${workspace.id}/phases/submit/`, { method: "POST", headers: { "Idempotency-Key": `web-phase-${workspace.id}-${workspace.revision}` }, body: JSON.stringify({ organization_id: workspace.organization_id, expected_revision: workspace.revision }) }),
  submissionInspection: (conferenceId: string, organizationId: string) =>
    request<SubmissionInspection>(`/api/v1/conferences/${conferenceId}/submission-inspection/?organization_id=${organizationId}`),
  reviewWorkspace: (assignmentId: string) => request<ReviewWorkspace>(`/api/v1/my/reviews/${assignmentId}/`),
  saveReview: (workspace: ReviewWorkspace, body: { answers: Record<string, number>; author_feedback: string; committee_comments: string }) =>
    request<{ id: string; revision: number }>(`/api/v1/conferences/${workspace.conference_id}/review-assignments/${workspace.assignment_id}/draft/`, { method: "POST", body: JSON.stringify({ organization_id: workspace.organization_id, rubric_version_id: workspace.rubric.id, expected_revision: workspace.draft?.revision ?? 0, ...body }) }),
  submitReview: (workspace: ReviewWorkspace) =>
    request<{ id: string; version: number }>(`/api/v1/conferences/${workspace.conference_id}/review-assignments/${workspace.assignment_id}/submit/`, { method: "POST", body: JSON.stringify({ organization_id: workspace.organization_id }) }),
  declareReviewConflict: (workspace: ReviewWorkspace, reason: string) =>
    request<{ assignment_id: string; conflict_id: string; state: string }>(`/api/v1/conferences/${workspace.conference_id}/review-assignments/${workspace.assignment_id}/conflict/`, { method: "POST", body: JSON.stringify({ organization_id: workspace.organization_id, reason }) }),
};
import { request } from "../api/client.js";

